import { useState } from 'react';

import cn from 'classnames';

import { Counter } from '../../Counter';

import { colors } from '../../../../data/constants';
import { LoaderProgress } from '../../LoaderProgress';
import { ButtonTip } from '../ButtonTip';

/**
 * Reusable button component with icon, text, and styling options
 *
 * @param {string} [props.text] - Button text
 * @param {Component} [props.icon] - Lucide icon (e.g., RefreshCcw)
 * @param {boolean} [props.isIconText] - **REQUIRED to show icon** - Must be true for icon display
 * @param {Function} [props.handleClick] - Click handler
 * @param {string} [props.tooltipText] - Tooltip on hover
 * @param {number} [props.size=14] - Icon size in pixels
 * @param {string} [props.color] - Icon color
 * @param {boolean} [props.isSmall] - Small padding variant
 *
 * @example
 * // Icon button
 * <ButtonTemplate icon={RefreshCcw} isIconText={true} handleClick={() => {}} />
 *
 * @example
 * // Text button
 * <ButtonTemplate text="Submit" handleClick={handleSubmit} />
 */
export const ButtonTemplate = ({
  type = 'button',
  text = '',
  tooltipText = '',
  isMobileTooltip = false,
  icon = false,
  isIconText = false,
  isTextHiddenMobile = false,
  isSmall = false,
  isSmallText = false,
  isSmallMobileText = false,
  bgColor = false,
  counter = false,
  disabled = false,
  isLoadingData = false,
  size,
  color,
  handleClick = () => {},
  classes = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = icon;

  const isBgColor = bgColor && bgColor.length > 0;
  const hasClasses = classes && classes.length > 0;
  const hasText = text && text.length > 0;

  return (
    <button
      className={cn(
        'relative flex justify-center items-center rounded text-black border-gray-200',
        { 'py-0.5 px-0.5 border-0': isSmall },
        { 'py-1.5 px-3 border-[1px]': !isSmall },
        { 'gap-1': isIconText },
        { 'text-white': isBgColor },
        { [bgColor]: isBgColor },
        { [classes]: hasClasses },
        { 'opacity-60': disabled || isLoadingData }
      )}
      onClick={handleClick}
      type={type}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled || isLoadingData}
    >
      {isIconText && (
        <Icon
          size={size ? size : 14}
          color={color ? color : colors.white}
        />
      )}

      {hasText && (
        <span
          className={cn(
            { 'text-sm': !isSmallText && !isSmallMobileText},
            { 'hidden md:block': isTextHiddenMobile },
            { 'text-xs md:text-sm': isSmallMobileText },
            { 'text-xs': isSmallText }
          )}
        >
          {text}
        </span>
      )}

      {isHovered && tooltipText && (
        <ButtonTip
          isMobileTooltip={isMobileTooltip}
          tooltipText={tooltipText}
        />
      )}

      {isLoadingData && <LoaderProgress />}

      {counter ? <Counter amount={counter} /> : null}
    </button>
  );
};
